/**
 * The derivation fold (PRD #792).
 *
 * This is the single place any number becomes a combat number. A class base
 * curve, an attribute allocation, equipment contributions, and active status
 * modifiers go in; one flat {@link CombatStats} record comes out, and every
 * consumer reads that record rather than any of the inputs. PRD #784 shipped
 * `CombatStats` as a seam reproducing the two inputs the old formula used, with
 * a note that attribute allocation and item affixes would replace the body of
 * one function. This module is that replacement.
 *
 * **The fold order is fixed, documented, and pinned by a test.**
 *
 * 1. The class base curve evaluated at the Hero's level.
 * 2. Attributes: the allocation plus any attributes equipment granted.
 * 3. Flat equipment contributions.
 * 4. Percentage equipment contributions.
 * 5. Status modifiers.
 *
 * The order is behavior, not convention: flat before percent means a plate
 * chest's `+50 health` is multiplied by a `+10% health` affix, and swapping the
 * two produces a different, documented number. An affix that grants an
 * attribute enters at step 2, because its whole point is to do what a spent
 * point does; an affix that grants a stat directly enters at step 3.
 *
 * **Attributes have one job each**, per PRD #713, and a test per attribute
 * proves both halves of that: the job it does, and that it moves nothing else.
 *
 * - Strength drives physical damage.
 * - Dexterity drives attack speed, cast speed, and crit chance.
 * - Intelligence drives spell damage and max mana.
 * - Vitality drives max health and health regeneration.
 * - Wisdom drives every resist and mana regeneration.
 *
 * The scalars in {@link ATTRIBUTE_TUNING} are **first-pass**. They are declared
 * once here rather than at call sites so a balance change has one place to
 * happen, and they are dialed by the tuning PRD (#799) against a harness that
 * does not exist yet. Do not defend these values; move them.
 *
 * This module knows nothing about Heroes. A `HeroCombatIdentity` satisfies
 * {@link CombatStatsSource} structurally and a monster supplies the same record
 * with no base curve and no attributes, so the kernel derives both without
 * importing a Hero concept. A guard test proves no module under
 * `src/sim/combat/` imports `hero.ts`.
 */

import { DAMAGE_TYPES, type DamageType } from './abilityCatalog';

export type WeaponGrip = 'one-handed' | 'two-handed';

/**
 * How much of one damage type a combatant turns away, in percentage points
 * (PRD #803). A hundred is immunity and a negative value is a vulnerability,
 * which is what lets one record serve both without a second field.
 */
export type DamageResists = Readonly<Record<DamageType, number>>;

/**
 * Build the complete resist record from whatever a source declared, so a read
 * site never has to ask whether a type is present. A malformed entry derives to
 * zero rather than to a `NaN` that would silently turn every hit of that type
 * into zero damage far from its cause.
 */
export function damageResists(
  source?: Partial<Record<DamageType, number>>,
): DamageResists {
  const resists = {} as Record<DamageType, number>;
  for (const type of DAMAGE_TYPES) {
    const value = source?.[type];
    resists[type] = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }
  return resists;
}

/** The five attributes, as the kernel sees them. `HeroStats` satisfies this structurally. */
export interface CombatAttributes {
  strength: number;
  dexterity: number;
  intelligence: number;
  vitality: number;
  wisdom: number;
}

export const ATTRIBUTE_KEYS: readonly (keyof CombatAttributes)[] = [
  'strength',
  'dexterity',
  'intelligence',
  'vitality',
  'wisdom',
];

export function noAttributes(): CombatAttributes {
  return {
    strength: 0,
    dexterity: 0,
    intelligence: 0,
    vitality: 0,
    wisdom: 0,
  };
}

/**
 * What one attribute point does. First-pass values owned by the tuning PRD
 * (#799); percentages are additive percentage points on a scalar of 1.
 */
export const ATTRIBUTE_TUNING = {
  /** Strength: physical damage. */
  strengthPhysicalDamagePercent: 1,
  /** Dexterity: attack speed, cast speed, crit chance. */
  dexterityAttackSpeedPercent: 0.5,
  dexterityCastSpeedPercent: 0.5,
  dexterityCritChancePercent: 0.2,
  /** Intelligence: spell damage, max mana. */
  intelligenceSpellDamagePercent: 1,
  intelligenceMaxManaFlat: 2,
  /** Vitality: max health, health regeneration. */
  vitalityMaxHealthFlat: 5,
  vitalityHealthRegenPerTick: 0.005,
  /** Wisdom: every resist, mana regeneration. Resist is percentage points. */
  wisdomResistPercent: 0.5,
  wisdomManaRegenPerTick: 0.005,
} as const;

/**
 * Bounds the fold applies to its own output. First-pass, tuning-PRD owned.
 *
 * The resist ceiling bounds what an allocation and equipment can *buy*, not
 * what content declares: a player who cannot be damaged is a stuck game, while
 * a monster authored immune to fire is a design decision PRD #803 already
 * handles by bounding the damage at zero rather than at a heal. The speed floor
 * keeps a scalar that a future debuff drives negative from inverting a
 * cooldown.
 */
export const DERIVED_STAT_BOUNDS = {
  /** Percentage points, so seventy-five is three quarters turned away. */
  maxResist: 75,
  minSpeedScalar: 0.1,
  maxCritChance: 1,
  /** What a crit multiplies damage by. Flat until an affix moves it. */
  baseCritMultiplier: 1.5,
} as const;

/**
 * A class base curve already evaluated at a level.
 *
 * Every field is optional because a monster declares none of them: it carries
 * its health on its world entity and its damage on its main hand, exactly as it
 * does today. An absent field folds as the baseline, so a two-field monster
 * source derives the same record it derived before this module existed.
 */
export interface CombatBaseStats {
  maxHealth?: number;
  maxMana?: number;
  healthRegenPerTick?: number;
  manaRegenPerTick?: number;
  critChance?: number;
  critMultiplier?: number;
  resists?: Partial<Record<DamageType, number>>;
}

/** The inputs derivation reads. `HeroCombatIdentity` satisfies this structurally. */
export interface CombatStatsSource {
  level: number;
  mainHand: { damage: number; grip: WeaponGrip };
  /** The Hero's allocated points. Absent for a combatant that allocates nothing. */
  attributes?: CombatAttributes;
  /** The class base curve at this level. Absent for a combatant with no class. */
  base?: CombatBaseStats;
  /**
   * Resists the source declares directly, in percentage points (PRD #803).
   * A monster carries its resists here rather than through a class curve, and
   * a source that declares both is folded once: the two are added.
   */
  resists?: Partial<Record<DamageType, number>>;
}

/**
 * One piece of equipment's contribution to the fold.
 *
 * Nothing populates this yet: item affixes are the itemization PRD (#795). It
 * is declared and folded here so that pipeline plugs into the same fold
 * attributes use rather than growing a second one, and the tests exercise it
 * with synthetic fixtures.
 */
export interface EquipmentContribution {
  /** Attributes the item grants. Folded with the allocation at step 2. */
  attributes?: Partial<CombatAttributes>;
  /** Flat stat contributions, step 3. */
  flat?: {
    maxHealth?: number;
    maxMana?: number;
    healthRegenPerTick?: number;
    manaRegenPerTick?: number;
    mainHandDamage?: number;
    critChance?: number;
    resists?: Partial<Record<DamageType, number>>;
  };
  /** Percentage contributions in additive percentage points, step 4. */
  percent?: {
    maxHealth?: number;
    maxMana?: number;
    physicalDamage?: number;
    spellDamage?: number;
    attackSpeed?: number;
    castSpeed?: number;
  };
}

/**
 * The status contribution to the fold, step 5.
 *
 * Only the modifiers that have no other read site enter here. `damageTaken` and
 * `damageDealt` deliberately do **not**: the kernel already folds those at its
 * own damage read sites, and folding them here too would apply them twice. The
 * split is the invariant, and a test pins it.
 */
export interface StatusStatContribution {
  resourceRegenFlat?: number;
}

/** The derived record every combat consumer reads. */
export interface CombatStats {
  /** Clamped to at least one, matching the `Math.max(1, level)` in the old formula. */
  level: number;
  /** The effective attributes: allocated plus granted. */
  attributes: CombatAttributes;
  mainHandDamage: number;
  /** Presentational today, since grip only selects a thrust clip. */
  mainHandGrip: WeaponGrip;
  /** Multiplier on physical damage dealt. 1 at baseline. */
  physicalDamageScalar: number;
  /** Multiplier on non-physical damage dealt. 1 at baseline. */
  spellDamageScalar: number;
  /** Divides authored cooldown ticks. 1 at baseline. */
  attackSpeedScalar: number;
  /** Divides authored wind-up ticks. 1 at baseline. */
  castSpeedScalar: number;
  /** Fraction in `[0, 1]`. Zero at baseline, so no shipped number rolls a crit. */
  critChance: number;
  critMultiplier: number;
  maxHealth: number;
  maxMana: number;
  healthRegenPerTick: number;
  manaRegenPerTick: number;
  /** Percentage points of incoming damage of that type removed, bounded below immunity. */
  resists: Record<DamageType, number>;
}

function finiteOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function nonNegative(value: number): number {
  return value > 0 ? value : 0;
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

function foldAttributes(
  allocated: CombatAttributes | undefined,
  contributions: readonly EquipmentContribution[],
): CombatAttributes {
  const attributes = noAttributes();
  for (const key of ATTRIBUTE_KEYS) {
    attributes[key] = nonNegative(finiteOr(allocated?.[key], 0));
  }
  for (const contribution of contributions) {
    for (const key of ATTRIBUTE_KEYS) {
      attributes[key] += finiteOr(contribution.attributes?.[key], 0);
    }
  }
  for (const key of ATTRIBUTE_KEYS) {
    attributes[key] = nonNegative(attributes[key]);
  }
  return attributes;
}

/**
 * Fold every input into the one record damage, vitals, and speed read.
 *
 * Defensive against malformed numbers by design, because the alternative to a
 * clamp is a `NaN` that silently turns every hit into zero damage far from its
 * cause. The level clamp is not defensive though: it is behavior, and it
 * reproduces the `Math.max(1, level)` the old damage formula applied.
 */
export function deriveCombatStats(
  source: CombatStatsSource,
  equipment: readonly EquipmentContribution[] = [],
  statuses: StatusStatContribution | null = null,
): CombatStats {
  const contributions = Array.isArray(equipment) ? equipment : [];

  // Step 1: the class base curve at this level.
  const base = source?.base ?? {};
  const level = Number.isSafeInteger(source?.level) ? Math.max(1, source.level) : 1;
  const stats: CombatStats = {
    level,
    attributes: noAttributes(),
    mainHandDamage: nonNegative(finiteOr(source?.mainHand?.damage, 0)),
    mainHandGrip:
      source?.mainHand?.grip === 'two-handed' ? 'two-handed' : 'one-handed',
    physicalDamageScalar: 1,
    spellDamageScalar: 1,
    attackSpeedScalar: 1,
    castSpeedScalar: 1,
    critChance: nonNegative(finiteOr(base.critChance, 0)),
    critMultiplier: finiteOr(
      base.critMultiplier,
      DERIVED_STAT_BOUNDS.baseCritMultiplier,
    ),
    maxHealth: nonNegative(finiteOr(base.maxHealth, 0)),
    maxMana: nonNegative(finiteOr(base.maxMana, 0)),
    healthRegenPerTick: nonNegative(finiteOr(base.healthRegenPerTick, 0)),
    manaRegenPerTick: nonNegative(finiteOr(base.manaRegenPerTick, 0)),
    resists: { ...damageResists() },
  };
  // Declared resists pass through unbounded: an authored monster immune to
  // fire is a content decision, and PRD #803 already bounds the damage it
  // mitigates at zero rather than at a heal. What this fold bounds is what a
  // player can *buy*, tracked separately below.
  for (const element of DAMAGE_TYPES) {
    stats.resists[element] =
      finiteOr(base.resists?.[element], 0) + finiteOr(source?.resists?.[element], 0);
  }
  const purchasedResists = damageResists() as Record<DamageType, number>;

  // Step 2: attributes, allocated plus granted. One job each.
  const attributes = foldAttributes(source?.attributes, contributions);
  stats.attributes = attributes;
  stats.physicalDamageScalar +=
    (attributes.strength * ATTRIBUTE_TUNING.strengthPhysicalDamagePercent) / 100;
  stats.attackSpeedScalar +=
    (attributes.dexterity * ATTRIBUTE_TUNING.dexterityAttackSpeedPercent) / 100;
  stats.castSpeedScalar +=
    (attributes.dexterity * ATTRIBUTE_TUNING.dexterityCastSpeedPercent) / 100;
  stats.critChance +=
    (attributes.dexterity * ATTRIBUTE_TUNING.dexterityCritChancePercent) / 100;
  stats.spellDamageScalar +=
    (attributes.intelligence * ATTRIBUTE_TUNING.intelligenceSpellDamagePercent) /
    100;
  stats.maxMana +=
    attributes.intelligence * ATTRIBUTE_TUNING.intelligenceMaxManaFlat;
  stats.maxHealth += attributes.vitality * ATTRIBUTE_TUNING.vitalityMaxHealthFlat;
  stats.healthRegenPerTick +=
    attributes.vitality * ATTRIBUTE_TUNING.vitalityHealthRegenPerTick;
  stats.manaRegenPerTick +=
    attributes.wisdom * ATTRIBUTE_TUNING.wisdomManaRegenPerTick;
  for (const element of DAMAGE_TYPES) {
    purchasedResists[element] +=
      attributes.wisdom * ATTRIBUTE_TUNING.wisdomResistPercent;
  }

  // Step 3: flat equipment contributions.
  for (const contribution of contributions) {
    const flat = contribution.flat;
    if (!flat) continue;
    stats.maxHealth += finiteOr(flat.maxHealth, 0);
    stats.maxMana += finiteOr(flat.maxMana, 0);
    stats.healthRegenPerTick += finiteOr(flat.healthRegenPerTick, 0);
    stats.manaRegenPerTick += finiteOr(flat.manaRegenPerTick, 0);
    stats.mainHandDamage += finiteOr(flat.mainHandDamage, 0);
    stats.critChance += finiteOr(flat.critChance, 0);
    for (const element of DAMAGE_TYPES) {
      purchasedResists[element] += finiteOr(flat.resists?.[element], 0);
    }
  }

  // Step 4: percentage equipment contributions, additive percentage points.
  let maxHealthPercent = 0;
  let maxManaPercent = 0;
  for (const contribution of contributions) {
    const percent = contribution.percent;
    if (!percent) continue;
    maxHealthPercent += finiteOr(percent.maxHealth, 0);
    maxManaPercent += finiteOr(percent.maxMana, 0);
    stats.physicalDamageScalar += finiteOr(percent.physicalDamage, 0) / 100;
    stats.spellDamageScalar += finiteOr(percent.spellDamage, 0) / 100;
    stats.attackSpeedScalar += finiteOr(percent.attackSpeed, 0) / 100;
    stats.castSpeedScalar += finiteOr(percent.castSpeed, 0) / 100;
  }
  stats.maxHealth *= 1 + maxHealthPercent / 100;
  stats.maxMana *= 1 + maxManaPercent / 100;

  // Step 5: status modifiers. Only the ones with no other read site.
  stats.manaRegenPerTick += finiteOr(statuses?.resourceRegenFlat, 0);

  stats.maxHealth = Math.round(nonNegative(stats.maxHealth));
  stats.maxMana = Math.round(nonNegative(stats.maxMana));
  stats.healthRegenPerTick = nonNegative(stats.healthRegenPerTick);
  stats.manaRegenPerTick = nonNegative(stats.manaRegenPerTick);
  stats.mainHandDamage = nonNegative(stats.mainHandDamage);
  stats.physicalDamageScalar = nonNegative(stats.physicalDamageScalar);
  stats.spellDamageScalar = nonNegative(stats.spellDamageScalar);
  stats.attackSpeedScalar = Math.max(
    DERIVED_STAT_BOUNDS.minSpeedScalar,
    stats.attackSpeedScalar,
  );
  stats.castSpeedScalar = Math.max(
    DERIVED_STAT_BOUNDS.minSpeedScalar,
    stats.castSpeedScalar,
  );
  stats.critChance = clamp(stats.critChance, 0, DERIVED_STAT_BOUNDS.maxCritChance);
  stats.critMultiplier = Math.max(1, stats.critMultiplier);
  // Only the purchased half is bounded. Wisdom and affixes cannot buy immunity,
  // because a player who cannot be damaged is not a balance problem but a stuck
  // game; an authored resist stays exactly as authored.
  for (const element of DAMAGE_TYPES) {
    stats.resists[element] += clamp(
      purchasedResists[element]!,
      -DERIVED_STAT_BOUNDS.maxResist,
      DERIVED_STAT_BOUNDS.maxResist,
    );
  }
  return stats;
}

/**
 * The scalar a payload of this element is multiplied by.
 *
 * Physical is Strength's job and everything else is Intelligence's, which is
 * how one damage formula serves a swing and a spell without either attribute
 * gaining a second job.
 */
export function damageScalarFor(stats: CombatStats, element: DamageType): number {
  return element === 'physical'
    ? stats.physicalDamageScalar
    : stats.spellDamageScalar;
}

/**
 * One type's resist, in percentage points. An absent record resists nothing.
 *
 * The null-tolerant signature is PRD #803's and is kept: the one damage-taken
 * read site calls this for a combatant it may not have.
 */
export function resistFor(
  stats: CombatStats | null | undefined,
  type: DamageType,
): number {
  const value = stats?.resists?.[type];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}
